import React, { useMemo } from 'react';
import DataGrid from '../../common/DataGrid';

/**
 * Invoice grid — column definitions only.
 * Before: 206 lines.  After: ~100 lines (more columns + custom renderers).
 * The StatusRenderer is invoice-specific logic so it stays here.
 */

const currencyFormatter = (params) => {
  if (params.value == null) return '';
  return params.value.toFixed(2);
};

const dateFormatter = (params) => {
  if (!params.value) return '';
  return new Date(params.value).toLocaleDateString();
};

const toLocalMidnight = (dateValue) => {
  if (!dateValue) return null;
  return new Date(`${String(dateValue).substring(0, 10)}T00:00:00`);
};

const StatusRenderer = (params) => {
  const { due_date, is_closed, is_void, is_sent } = params.data;
  let status = 'Draft';
  let colorClass = 'text-blue-500 bg-blue-50';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = toLocalMidnight(due_date);

  if (is_closed) {
    status = 'Paid';
    colorClass = 'text-emerald-600 bg-emerald-50';
  } else if (is_sent) {
    status = 'Sent';
    colorClass = 'text-amber-600 bg-amber-50';
  } else if (is_void) {
    status = 'Void';
    colorClass = 'text-gray-500 bg-gray-100';
  } else if (due && due.getTime() === today.getTime()) {
    status = 'Due today';
    colorClass = 'text-orange-500 bg-orange-50';
  } else if (due && due < today) {
    const diffDays = Math.round((today - due) / (1000 * 60 * 60 * 24));
    status = `Overdue ${diffDays} days`;
    colorClass = 'text-red-500 bg-red-50';
  }

  return (
    <div className="flex items-center h-full">
      <span className={`px-2 py-0.5 rounded-xl text-xs font-semibold flex items-center gap-1 ${colorClass}`}>
        {status.includes('Overdue') && <span className="text-sm">!</span>}
        {status}
      </span>
    </div>
  );
};

export default function InvoiceGrid(props) {
  const columns = useMemo(
    () => [
      { field: 'invoice_date', headerName: 'Date', maxWidth: 120, valueFormatter: dateFormatter },
      { field: 'invoice_number', headerName: 'No.', maxWidth: 100 },
      {
        headerName: 'Customer',
        maxWidth: 180,
        flex: 2,
        valueGetter: (params) =>
          params.data.customers?.full_name || params.data.customer_name || 'Unknown',
      },
      { field: 'total_amount', headerName: 'Amount', valueFormatter: currencyFormatter, maxWidth: 130, type: 'rightAligned' },
      {
        field: 'total_paid',
        headerName: 'Paid',
        maxWidth: 130,
        valueFormatter: currencyFormatter,
        type: 'rightAligned',
      },
      {
        field: 'amount_due',
        headerName: 'Due',
        maxWidth: 130,
        valueFormatter: currencyFormatter,
        type: 'rightAligned',
        },
      { field: 'due_date', headerName: 'Due Date', maxWidth: 130, valueFormatter: dateFormatter },
      { headerName: 'Status', cellRenderer: StatusRenderer, minWidth: 150 },
    ],
    []
  );

  return (
    <DataGrid
      {...props}
      columns={columns}
      exportFileName="invoices.csv"
      editLabel="View/Edit"
      pagination
      paginationSize={20}
    />
  );
}
